import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";

const COMPLIANCE_STATUSES = [
  "COMPLIANT",
  "AT_RISK",
  "NON_COMPLIANT",
  "OVERDUE",
  "INSUFFICIENT_EVIDENCE",
  "NEEDS_REVIEW",
  "NOT_APPLICABLE",
  "ERROR",
];

const STATUS_LABELS = {
  COMPLIANT: "Compliant",
  AT_RISK: "At Risk",
  NON_COMPLIANT: "Non Compliant",
  OVERDUE: "Overdue",
  INSUFFICIENT_EVIDENCE: "Insufficient Evidence",
  NEEDS_REVIEW: "Needs Review",
  NOT_APPLICABLE: "Not Applicable",
  ERROR: "Error",
};

const asList = (payload) =>
  Array.isArray(payload) ? payload : payload?.content || payload?.data || payload?.items || [];

const statusKey = (value) => {
  const normalized = String(value || "NEEDS_REVIEW")
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");
  if (normalized === "PASS" || normalized === "PASSED") return "COMPLIANT";
  if (normalized === "FAIL" || normalized === "FAILED") return "NON_COMPLIANT";
  if (normalized === "ACTIVE" || normalized === "PENDING" || normalized === "REVIEW") return "NEEDS_REVIEW";
  if (normalized === "INACTIVE") return "NOT_APPLICABLE";
  return COMPLIANCE_STATUSES.includes(normalized) ? normalized : "NEEDS_REVIEW";
};

const statusTone = (status) => {
  if (status === "COMPLIANT") return "green";
  if (["NON_COMPLIANT", "OVERDUE", "ERROR"].includes(status)) return "red";
  if (["AT_RISK", "INSUFFICIENT_EVIDENCE", "NEEDS_REVIEW"].includes(status)) return "amber";
  if (status === "NOT_APPLICABLE") return "gray";
  return "blue";
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const policyName = (policy) =>
  firstText(
    policy.compliancePolicyName,
    policy.compliancePolicyTitle,
    policy.inventoryItemName,
    policy.inventoryItemTitle,
    policy.name,
    policy.title,
    policy.complianceInventoryItem?.name,
    policy.complianceInventoryItem?.title,
    policy.inventoryItem?.name,
    policy.inventoryItem?.title,
    "Compliance policy",
  );

const formatDateTime = (value) => {
  if (!value) return "Not run";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

export default function ComplianceDashboardPage() {
  const { selectedWorkspaceId, selectedWorkspace } = useWorkspace();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadPolicies = () => {
    setLoading(true);
    setError("");
    if (!selectedWorkspaceId) {
      setPolicies([]);
      setLoading(false);
      return;
    }
    careService.workspaceCompliancePolicies
      .list(selectedWorkspaceId, { wid: selectedWorkspaceId })
      .then((payload) => setPolicies(asList(payload)))
      .catch((err) => {
        setPolicies([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load compliance dashboard.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPolicies();
  }, [selectedWorkspaceId]);

  const stats = useMemo(() => {
    const enabled = policies.filter((policy) => policy.enabled !== false);
    const statusCounts = COMPLIANCE_STATUSES.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
    enabled.forEach((policy) => {
      statusCounts[statusKey(policy.status)] += 1;
    });
    const applicableTotal = enabled.length - statusCounts.NOT_APPLICABLE;
    const compliantTotal = statusCounts.COMPLIANT;
    const complianceRate = applicableTotal > 0 ? Math.round((compliantTotal / applicableTotal) * 100) : 0;
    const attentionTotal =
      statusCounts.AT_RISK +
      statusCounts.NON_COMPLIANT +
      statusCounts.OVERDUE +
      statusCounts.INSUFFICIENT_EVIDENCE +
      statusCounts.NEEDS_REVIEW +
      statusCounts.ERROR;

    return {
      total: policies.length,
      enabled: enabled.length,
      disabled: policies.length - enabled.length,
      applicableTotal,
      complianceRate,
      attentionTotal,
      statusCounts,
    };
  }, [policies]);

  const attentionPolicies = useMemo(
    () =>
      policies
        .filter((policy) => policy.enabled !== false)
        .filter((policy) => !["COMPLIANT", "NOT_APPLICABLE"].includes(statusKey(policy.status)))
        .slice(0, 6),
    [policies],
  );

  return (
    <CareShell className="dashboard-content app-page-content compliance-dashboard-page">
      <section className="compliance-dashboard-header">
        <div className="compliance-dashboard-header__copy">
          <span>Compliance Dashboard</span>
          <h1>{selectedWorkspace?.name || "Facility"}</h1>
          <p>Current compliance posture for enabled facility policies.</p>
        </div>
        <div className="compliance-dashboard-header__score" aria-label="Compliance summary">
          <div>
            <span>Compliance Rate</span>
            <strong>{stats.complianceRate}%</strong>
            <small>{stats.statusCounts.COMPLIANT} of {stats.applicableTotal} applicable policies</small>
          </div>
          <button className="mini-btn primary" type="button" onClick={loadPolicies} disabled={loading}>
            <i className={`ti ${loading ? "ti-loader-2" : "ti-refresh"}`} /> Refresh
          </button>
        </div>
      </section>

      {error ? <p className="form-error">{error}</p> : null}

      <section className="compliance-dashboard-stats">
        <article>
          <i className="ti ti-clipboard-check" />
          <span>Enabled</span>
          <strong>{stats.enabled}</strong>
          <small>{stats.disabled} off</small>
        </article>
        <article>
          <i className="ti ti-alert-triangle" />
          <span>Attention</span>
          <strong>{stats.attentionTotal}</strong>
          <small>Risk or review</small>
        </article>
        <article>
          <i className="ti ti-clock-exclamation" />
          <span>Overdue</span>
          <strong>{stats.statusCounts.OVERDUE}</strong>
          <small>Past deadline</small>
        </article>
        <article>
          <i className="ti ti-file-search" />
          <span>Evidence</span>
          <strong>{stats.statusCounts.INSUFFICIENT_EVIDENCE}</strong>
          <small>Gaps found</small>
        </article>
      </section>

      <div className="compliance-dashboard-layout">
        <Panel title="Status Breakdown">
          <div className="compliance-status-breakdown">
            {COMPLIANCE_STATUSES.map((status) => {
              const count = stats.statusCounts[status];
              const percent = stats.enabled ? Math.round((count / stats.enabled) * 100) : 0;
              return (
                <div className="compliance-status-row" key={status}>
                  <span><em className={`pill ${statusTone(status)}`}>{STATUS_LABELS[status]}</em></span>
                  <strong>{count}</strong>
                  <div aria-hidden="true"><b style={{ width: `${percent}%` }} /></div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="Policies Needing Attention" actions={<Link className="text-action" to="/compliance/inventory">View policies</Link>}>
          <div className="resident-tab-list compliance-attention-list" style={{ "--resident-record-columns": "minmax(220px, 1fr) 145px 145px" }}>
            <div className="resident-tab-list-row resident-tab-list-head-row">
              <span>Policy</span>
              <span>Status</span>
              <span>Latest run</span>
            </div>
            {attentionPolicies.map((policy) => {
              const status = statusKey(policy.status);
              return (
                <article className="resident-tab-list-row" key={policy.id || policy.policyId || policyName(policy)}>
                  <span>
                    <strong>{policyName(policy)}</strong>
                    <small>{firstText(policy.compliancePolicyCode, policy.inventoryItemCode, policy.code, policy.compliancePolicy?.code, policy.complianceInventoryItem?.code)}</small>
                  </span>
                  <span><em className={`pill ${statusTone(status)}`}>{STATUS_LABELS[status]}</em></span>
                  <span>{formatDateTime(policy.latestRunAt || policy.lastRunAt || policy.recentRun?.runAt)}</span>
                </article>
              );
            })}
            {loading ? <div className="resident-inline-empty">Loading compliance dashboard...</div> : null}
            {!loading && !attentionPolicies.length ? <div className="resident-inline-empty">No enabled policies need attention.</div> : null}
          </div>
        </Panel>
      </div>
    </CareShell>
  );
}
